import React, { useState } from "react"
import { useTranslation, Trans } from "react-i18next"
import { Formik, Form, Field, useField } from "formik"
import clsx from "clsx"
import _ from "lodash"

import { useSearchParams, useLocation, useNavigate, useParams, Link } from "../navigation"
import { DatePicker } from '../components/input/DatePicker'
import { FilingSelection } from '../components/FilingSelection'
import Modal from "../components/Modal"
import { countryCodes, ipTypes, competitorMemberStates, epCountries } from '../data'
import Trinary from "../components/input/Trinary"
import { useExternal } from "./"
import { competitorMemberUrl } from "./utils"
import { CompetitorMember, CompetitorAgentLink } from "./external"
import { useAgents } from "../agents/AgentsProvider"
import { nameOf, extractAgents } from "../agents/utils"
import { splitAgents } from "../patents/utils"
import { AddAgentMask, AddAgentButton } from "../agents/AddAgent"
import { TagListField } from "../components/TagList"
import { linkDifference } from "../BackendProvider"
import { Agent } from "../agents/utils"

const labelStyle = "label"

export default function EditFamilyMember() {
    const { t } = useTranslation()

    const { internalReference } = useParams()
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const navigate = useNavigate()

    const { members, memberByReference, families, familyById, postMember, agentsByMemberId: competitorAgentsByMemberId, postCompetitorAgentLink, deleteCompetitorAgentLink, isLoading } = useExternal()
    const { agentById, agents } = useAgents()

    const member = memberByReference[internalReference]
    const otherMembers = new Set(members.map(m => m.internalReference).filter(m => m !== internalReference))
    const isAdding = member === undefined

    const [showInventorAddMask, setShowInventorAddMask] = useState(false)
    const [showApplicantAddMask, setShowApplicantAddMask] = useState(false)
    const [showOwnerAddMask, setShowOwnerAddMask] = useState(false)

    const splittedAgents = splitAgents({ agents, allowedAgents: ['person', 'company'] })
    const tagDisplays = _.mapValues(agentById, a => nameOf(a))
    const companies = (splittedAgents.company ?? []).map((a: Agent) => a.agentId).sort((a: number, b: number) => tagDisplays[a]?.localeCompare(tagDisplays[b]))
    const persons = (splittedAgents.person ?? []).map((a: Agent) => a.agentId).sort((a: number, b: number) => tagDisplays[a]?.localeCompare(tagDisplays[b]))

    const competitorMemberId = member?.competitorMemberId
    const currentInventors = extractAgents('inventor', competitorMemberId, competitorAgentsByMemberId, agentById)
    const currentOwners = extractAgents('owner', competitorMemberId, competitorAgentsByMemberId, agentById)
    const currentApplicants = extractAgents('applicant', competitorMemberId, competitorAgentsByMemberId, agentById)

    const competitorFamilyId = member?.competitorFamilyId ?? parseInt(searchParams.get('competitorFamilyId'))
    const family = families.find(f => f.competitorFamilyId === competitorFamilyId)

    const copyFromRef = searchParams.get('copyFrom')
    const srcMember = (members.find(m => m.internalReference === copyFromRef) ?? location.state) ?? {}
    const isCopying = srcMember?.competitorMemberId !== undefined
    const copyAgentsFrom = (link) =>
        isCopying
            ? extractAgents(link, srcMember?.competitorMemberId, competitorAgentsByMemberId, agentById)
            : []

    const initialValues: CompetitorMember & { inventors: any[], owners: any[], applicants: any[] } = !isAdding
        ? { ...member, inventors: currentInventors, owners: currentOwners, applicants: currentApplicants }
        : {
            title: "",
            countryCode: "WO",
            internalReference: "",
            externalReference: undefined,
            competitorMemberStatus: 'relevant',
            ipType: 'patent',
            numberClaims: undefined,
            applicationNumber: undefined,
            applicationDate: undefined,
            publicationNumber: undefined,
            publicationDate: undefined,
            patentNumber: undefined,
            patentDate: undefined,
            expiryDate: undefined,
            abandonmentDate: undefined,
            patentOfficeLink: undefined,
            validated: undefined,
            pctRouteFiling: undefined,
            firstFiling: undefined,
            unitaryPatent: undefined,
            unitaryPatentRegistrationDate: undefined,
            optOut: undefined,
            competitorFamilyId,
            inventors: copyAgentsFrom('inventor'),
            applicants: copyAgentsFrom('applicant'),
            owners: copyAgentsFrom('owner'),
            ...(!isCopying ? {} : {
                title: srcMember.title,
                countryCode: srcMember.countryCode,
                competitorMemberStatus: srcMember.competitorMemberStatus,
                ipType: srcMember.ipType,
            }),
        }

    async function updateLinks(currentLinks: (number | string)[], newLinks: (number | string)[], linkType: string, competitorMemberId: number) {
        const asLink = (id: number | string) => ({
            realm: "",
            competitorMemberId,
            agentId: +id,
            linkType,
        } as CompetitorAgentLink)
        if (newLinks) {
            const [toAdd, toDelete] = linkDifference(currentLinks.map(asLink), newLinks.map(asLink),
                (a, b) => a.competitorMemberId === b.competitorMemberId && a.agentId === b.agentId && a.linkType === b.linkType)
            if (toDelete.length > 0)
                await deleteCompetitorAgentLink(toDelete)
            if (toAdd.length > 0)
                await postCompetitorAgentLink(toAdd)
        }
        return Promise.resolve({})
    }

    const onSubmit = (values: CompetitorMember & { inventors: any[], applicants: any[], owners: any[] }, { setSubmitting }) => {
        const numberClaims = (values.numberClaims === undefined || values.numberClaims === null || isNaN(values.numberClaims)) ? undefined : parseInt(String(values.numberClaims))
        const memberId = isAdding ? undefined : values.competitorMemberId
        postMember({ ...values, numberClaims, competitorMemberId: memberId })
            .then(async (resp: CompetitorMember) => {
                const cmId = resp.competitorMemberId
                await updateLinks(currentInventors, values.inventors, 'inventor', cmId)
                await updateLinks(currentApplicants, values.applicants, 'applicant', cmId)
                await updateLinks(currentOwners, values.owners, 'owner', cmId)
                return resp
            })
            .then((resp: CompetitorMember) => {
                navigate(competitorMemberUrl(resp))
            })
            .catch(err => { console.warn(err); setSubmitting(false) })
    }

    return (isLoading
        ? null
        : <Modal escAction={() => (!showApplicantAddMask && !showInventorAddMask && !showOwnerAddMask) && navigate(-1)}>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validateOnBlur={false}
                validateOnChange={true}
                validate={({ internalReference }) => {
                    const errors: Record<string, string> = {}
                    if (internalReference === undefined || internalReference === "")
                        errors.internalReference = t('reference-required')
                    else if (otherMembers.has(internalReference))
                        errors.internalReference = t('reference-used')
                    return errors
                }}
                onSubmit={onSubmit}
            >{({ values: { inventors, owners, applicants, countryCode, unitaryPatent, validated }, setFieldValue, isSubmitting }) => {
                const canBeValidated = epCountries.indexOf(countryCode) >= 0
                const canOptOut = (canBeValidated && validated !== false) || (countryCode === "EP" && !unitaryPatent)
                const inventorAddMask = <AddAgentMask {...{
                    title: t('create-inventor'),
                    setShowMask: setShowInventorAddMask,
                    withCompanies: false,
                    update: (v) => setFieldValue('inventors', [...inventors, v])
                }} />
                const ownerAddMask = <AddAgentMask {...{
                    title: t('create-owner'),
                    setShowMask: setShowOwnerAddMask,
                    update: (v) => setFieldValue('owners', [...owners, v])
                }} />
                const applicantAddMask = <AddAgentMask {...{
                    title: t('create-applicant'),
                    setShowMask: setShowApplicantAddMask,
                    update: (v) => setFieldValue('applicants', [...applicants, v])
                }} />
                return (
                    <div className="max-w-4xl space-y-2">
                        <h4 className="px-4 pt-4 pb-0 text-center">
                            {isAdding ? (
                                <Trans i18nKey="add-to-family-name" values={{ name: `${family?.internalReference} - ${family?.familyName}` }} />
                            ) : (
                                <Trans i18nKey="edit-family-member-name" values={{ name: internalReference }} />
                            )}
                        </h4>
                        {isCopying && (
                            <div className="text-center text-sm text-gray-500">
                                {t('copy-from-name', { name: `${srcMember.internalReference} (${srcMember.countryCode})` })}
                            </div>
                        )}
                        <Form>
                            <div className="p-4 pt-2 grid sm:grid-cols-3 grid-cols-1 gap-2 md:gap-x-4">
                                <RichField name="title" className="sm:col-span-3">
                                    <Field className="h-8 px-2 form-input" />
                                </RichField>

                                <RichField name="countryCode">
                                    <Field className="h-8 px-2 py-1 form-select" as="select">
                                        {countryCodes.map(value => (
                                            <option key={value} value={value}>{value}</option>
                                        ))}
                                    </Field>
                                </RichField>

                                <RichField name="internalReference">
                                    <Field
                                        className="h-8 px-2 form-input"
                                        required
                                        pattern="[a-zA-Z0-9 ._\-]*"
                                        title={t('use-usernames')}
                                    />
                                </RichField>

                                <RichField name="externalReference" label="ext-reference">
                                    <Field className="h-8 px-2 form-input" />
                                </RichField>

                                <RichField name="competitorMemberStatus" label="status">
                                    <Field className="h-8 px-2 py-1 form-select" as="select">
                                        {competitorMemberStates.map(s => (
                                            <option key={s} value={s}>{t(s)}</option>
                                        ))}
                                    </Field>
                                </RichField>

                                <RichField name="ipType">
                                    <Field className="h-8 px-2 py-1 form-select" as="select">
                                        {ipTypes.map(value => (
                                            <option key={value} value={value}>{t(value)}</option>
                                        ))}
                                    </Field>
                                </RichField>

                                <RichField name="numberClaims" label="num-claims">
                                    <Field className="h-8 px-2 form-input" type="number" min="0" />
                                </RichField>

                                <div className="flex flex-col gap-2 justify-between">
                                    <RichField name="applicationNumber">
                                        <Field className="h-8 px-2 form-input" />
                                    </RichField>
                                    <RichField name="applicationDate">
                                        <Field className="h-8 px-1 form-input" as={DatePicker} />
                                    </RichField>
                                </div>

                                <div className="flex flex-col gap-2 justify-between">
                                    <RichField name="publicationNumber">
                                        <Field className="h-8 px-2 form-input" />
                                    </RichField>
                                    <RichField name="publicationDate">
                                        <Field className="h-8 px-1 form-input" as={DatePicker} />
                                    </RichField>
                                </div>

                                <div className="flex flex-col gap-2 justify-between">
                                    <RichField name="patentNumber">
                                        <Field className="h-8 px-2 form-input" />
                                    </RichField>
                                    <RichField name="patentDate">
                                        <Field className="h-8 px-1 form-input" as={DatePicker} />
                                    </RichField>
                                </div>

                                <RichField name="patentOfficeLink" className="sm:col-span-3">
                                    <Field className="h-8 px-2 form-input" type="url" />
                                </RichField>

                                {/* Filing, Opt Out, Unitary Patent */}
                                <label>
                                    <span className={labelStyle}>{t('filing')}</span>
                                    <FilingSelection />
                                </label>

                                <label className="inline-flex gap-2 items-center">
                                    <Trinary name="optOut" disabled={!canOptOut} />
                                    <span className={clsx(labelStyle, !canOptOut && 'text-slate-200')}>{t('optOut')}</span>
                                </label>

                                <label className="inline-flex gap-2 items-center">
                                    <Trinary name="unitaryPatent" disabled={countryCode !== 'EP'} />
                                    <span className={clsx(labelStyle, countryCode !== 'EP' && 'text-slate-200')}>{t('unitaryPatent')}</span>
                                </label>

                                {/* Dates */}
                                <RichField name="expiryDate">
                                    <Field className="h-8 px-1 form-input" as={DatePicker} />
                                </RichField>

                                <RichField name="abandonmentDate">
                                    <Field className="h-8 px-1 form-input" as={DatePicker} />
                                </RichField>

                                <RichField name="unitaryPatentRegistrationDate">
                                    <Field className={clsx("h-8 px-1 form-input", !unitaryPatent && "opacity-30")} as={DatePicker} />
                                </RichField>

                                <div className="sm:col-span-3">
                                    <RichField name="inventors">
                                        <TagListField
                                            {...{
                                                availableTags: persons,
                                                tagDisplays,
                                                name: 'inventors',
                                                placeholder: t('add'),
                                                addButton: <AddAgentButton {...{ setShowMask: setShowInventorAddMask }} />,
                                            }}
                                        />
                                    </RichField>
                                </div>

                                <div className="sm:col-span-3">
                                    <RichField name="applicants">
                                        <TagListField
                                            {...{
                                                availableTags: { Companies: companies, Persons: persons },
                                                tagDisplays,
                                                name: 'applicants',
                                                placeholder: t('add'),
                                                addButton: <AddAgentButton {...{ setShowMask: setShowApplicantAddMask }} />,
                                            }}
                                        />
                                    </RichField>
                                </div>

                                <div className="sm:col-span-3">
                                    <RichField name="owners">
                                        <TagListField
                                            {...{
                                                availableTags: { Companies: companies, Persons: persons },
                                                tagDisplays,
                                                name: 'owners',
                                                placeholder: t('add'),
                                                addButton: <AddAgentButton {...{ setShowMask: setShowOwnerAddMask }} />,
                                            }}
                                        />
                                    </RichField>
                                </div>
                            </div>

                            <div className="flex flex-row-reverse gap-4 bg-pc-200 p-4 col-span-3">
                                <input
                                    type="submit"
                                    className={clsx('btn-primary', isSubmitting && 'animate-pulse')}
                                    disabled={isSubmitting}
                                    value={t('save')}
                                />
                                {/* @ts-ignore */}
                                <Link to={-1} className="btn-secondary">
                                    {t('cancel')}
                                </Link>
                            </div>
                        </Form>
                        {showInventorAddMask && inventorAddMask}
                        {showOwnerAddMask && ownerAddMask}
                        {showApplicantAddMask && applicantAddMask}
                    </div>
                )
            }}</Formik>
        </Modal>
    )
}

function RichField({ name, label = undefined, className = '', children }) {
    const { t } = useTranslation()
    // eslint-disable-next-line
    const [field, meta] = useField(name)
    return (
        <div className={"flex flex-col " + className}>
            <label htmlFor={name} aria-label={label ?? name}>
                <span className={labelStyle}>{t(label ?? name)}</span>
            </label>
            {React.cloneElement(children, { name, id: name })}
            {meta.touched && meta.error ? <div className="text-red-600">{meta.error}</div> : null}
        </div>
    )
}

